/* ==============================================
   ARTICLES — the one place a piece is described.
   The home page, the career centre page, /insights
   and the article page itself all read from here,
   so adding a piece is one entry rather than four
   edits.

   Prose is NOT here. Every piece is written as
   markdown in src/content/articles/ and compiled
   into article-bodies.js by scripts/build-articles.mjs,
   which runs before `npm start` and `npm run build`.
   Title, category, read time and standfirst are
   read back off that file rather than retyped, so
   the card and the page can never disagree.

   To add a piece:
     1. Write src/content/articles/NN-the-slug.md
        (see the header comment in build-articles.mjs
        for the shape).
     2. Add the slug's tone to article-tones.json and
        drop a cover at src/assets/articles/<slug>.jpg,
        then import it below. Covers are photographs
        now; the drawn SVGs that scripts/build-article-
        covers.mjs generates are no longer what ships,
        so that script only matters if you go back to
        illustrated covers.
     3. Add an entry to INDEX below. Order here is
        the order on /insights, and the first three
        are the ones the home page features.
     4. Drop the PDF at public/articles/<slug>.pdf
        if there is one.
   ============================================== */

import { bodies } from "./article-bodies";
import tones from "./article-tones.json";

import coverJuniorMid from "../assets/articles/the-junior-and-mid-rungs-are-merging.webp";
import coverCareerFair from "../assets/articles/your-career-fair-is-tier-one-or-invisible.webp";
import coverGradsReady from "../assets/articles/employers-say-grads-are-ready.webp";
import coverRoi from "../assets/articles/only-6-percent-of-campus-recruiting-teams-can-prove-roi.webp";
import coverGpa from "../assets/articles/lead-with-your-gpa-is-dead.webp";
import coverAi from "../assets/articles/everyone-says-students-use-ai-for-everything.webp";
import coverConversion from "../assets/articles/what-intern-to-hire-conversion-actually-tells-you.webp";
import coverCutting from "../assets/articles/employers-cutting-hiring-arent-citing-your-graduates.webp";
import coverFifteen from "../assets/articles/pick-your-fifteen-employers.webp";
import coverStandards from "../assets/articles/your-listing-standards-are-policy.webp";
import coverVirtual from "../assets/articles/students-said-they-wanted-virtual-fairs.webp";
import coverEarnings from "../assets/articles/what-the-earnings-accountability-rule-measures.webp";
import coverH1b from "../assets/articles/what-the-h1b-changes-mean-for-advising-international-students.webp";
import coverStem from "../assets/articles/do-stem-for-the-visa-runway-just-inverted.webp";
import coverSkills from "../assets/articles/what-an-employer-means-by-skills-based-hiring.webp";

const COVERS = {
  "the-junior-and-mid-rungs-are-merging": coverJuniorMid,
  "your-career-fair-is-tier-one-or-invisible": coverCareerFair,
  "employers-say-grads-are-ready": coverGradsReady,
  "only-6-percent-of-campus-recruiting-teams-can-prove-roi": coverRoi,
  "lead-with-your-gpa-is-dead": coverGpa,
  "everyone-says-students-use-ai-for-everything": coverAi,
  "what-intern-to-hire-conversion-actually-tells-you": coverConversion,
  "employers-cutting-hiring-arent-citing-your-graduates": coverCutting,
  "pick-your-fifteen-employers": coverFifteen,
  "your-listing-standards-are-policy": coverStandards,
  "students-said-they-wanted-virtual-fairs": coverVirtual,
  "what-the-earnings-accountability-rule-measures": coverEarnings,
  "what-the-h1b-changes-mean-for-advising-international-students": coverH1b,
  "do-stem-for-the-visa-runway-just-inverted": coverStem,
  "what-an-employer-means-by-skills-based-hiring": coverSkills,
};

/* Slug, plus the two things the markdown can't carry: who the piece is
   written for (the /insights filter reads it) and, where one exists, a
   one-line note for the reader that the piece is a summary rather than
   compliance guidance. `date` is deliberately absent — nothing here has a
   publication date yet, and the page hides the line until one is set. */
const INDEX = [
  { slug: "the-junior-and-mid-rungs-are-merging", audience: ["career-centers", "job-seekers"] },
  { slug: "your-career-fair-is-tier-one-or-invisible", audience: ["career-centers"] },
  { slug: "employers-say-grads-are-ready", audience: ["career-centers"] },
  { slug: "only-6-percent-of-campus-recruiting-teams-can-prove-roi", audience: ["career-centers"] },
  { slug: "lead-with-your-gpa-is-dead", audience: ["career-centers", "job-seekers"] },
  { slug: "everyone-says-students-use-ai-for-everything", audience: ["career-centers", "job-seekers"] },
  { slug: "what-intern-to-hire-conversion-actually-tells-you", audience: ["career-centers"] },
  { slug: "employers-cutting-hiring-arent-citing-your-graduates", audience: ["career-centers"] },
  { slug: "pick-your-fifteen-employers", audience: ["career-centers"] },
  { slug: "your-listing-standards-are-policy", audience: ["career-centers"] },
  { slug: "students-said-they-wanted-virtual-fairs", audience: ["career-centers"] },
  {
    slug: "what-the-earnings-accountability-rule-measures",
    audience: ["career-centers"],
    note: "A summary for career services staff, not legal or compliance guidance.",
  },
  {
    slug: "what-the-h1b-changes-mean-for-advising-international-students",
    audience: ["career-centers", "job-seekers"],
    note: "A summary for career services staff. Not legal advice, and both rules are under litigation.",
  },
  { slug: "do-stem-for-the-visa-runway-just-inverted", audience: ["career-centers", "job-seekers"] },
  { slug: "what-an-employer-means-by-skills-based-hiring", audience: ["career-centers", "job-seekers"] },
];

/* The published record: index entry + whatever the markdown carries. */
export const articles = INDEX.map(({ slug, audience, note, date }) => {
  const body = bodies[slug];
  if (!body) {
    throw new Error(
      `articles.js: no markdown for "${slug}". Add src/content/articles/NN-${slug}.md and re-run scripts/build-articles.mjs.`
    );
  }

  return {
    slug,
    audience,
    note,
    date,
    tone: tones[slug] || "blue",
    cover: COVERS[slug],
    title: body.title,
    kicker: body.kicker,
    readTime: body.readTime,
    dek: body.dek,
    body: body.body,
    sources: body.sources,
    /* The PDF each piece went out as, sitting in public/articles. Nothing
       links to it since the download button came off the article page —
       kept so the files stay addressable if a link is wanted again. */
    pdf: `/articles/${slug}.pdf`,
    url: `/insights/${slug}`,
  };
});

export const bySlug = (slug) => articles.find((a) => a.slug === slug);

/* The next two pieces after this one, wrapping at the end of the shelf, so
   the foot of an article is never a dead end. */
export const relatedTo = (slug, count = 3) => {
  const i = articles.findIndex((a) => a.slug === slug);
  if (i < 0) return articles.slice(0, count);
  return Array.from({ length: count }, (_, n) => articles[(i + n + 1) % articles.length]);
};

/* Audience keys, with the label each one carries in the filter. */
export const audiences = [
  { key: "all", label: "All" },
  { key: "job-seekers", label: "For job seekers" },
  { key: "career-centers", label: "For career centers" },
];

export const articlesFor = (audience) =>
  !audience || audience === "all"
    ? articles
    : articles.filter((a) => a.audience.includes(audience));

/* One link label for every card. This used to read "Read the <kicker>", which
   put the Analysis/Explainer split in front of the reader twice — once as the
   category label above the title and again down here. The cards no longer show
   the category at all, so the link doesn't name it either. */
export const ctaFor = () => "Read the article";

/* An internal path is routed; anything else is an outbound link. Components
   use this so a published piece can live anywhere without a code change. */
export const isExternal = (url) => !url.startsWith("/");

export default articles;
